/**
 * Audit Website Detail Page
 *
 * Detail view for CyberSweep audit websites (CSAW records).
 * Fetches data from Gobodo/RDS (Cyber_Sweep_Audit_Webs__stg5_c).
 * Shows Related and Details tabs matching Salesforce layout.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  Globe,
  FileText,
  AlertCircle,
  Edit2,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useAuditWebsite, AuditWebsiteDetailRecord } from '../../services/queries/cyberSweepQueries';

interface AuditWebsiteDetailProps {
  websiteId: string;
  onBack: () => void;
  onTemplateClick?: (templateId: string) => void;
}

type TabType = 'related' | 'details';

const AuditWebsiteDetail: React.FC<AuditWebsiteDetailProps> = ({ websiteId, onBack, onTemplateClick }) => {
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Fetch from Gobodo
  const { data: websiteData, isLoading, error } = useAuditWebsite(websiteId);

  // Format date for display
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !websiteData) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Audit Website Not Found
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ID "{websiteId}" not found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        {/* Title + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded">
              <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Cyber Sweep Audit Website
              </span>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {websiteData.name || websiteId}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600">
              <Edit2 className="h-3.5 w-3.5 inline mr-1" />
              Edit
            </button>
            <button className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="h-3.5 w-3.5 inline mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('related')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'related'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Related
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Details
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'related' ? (
        <RelatedTab />
      ) : (
        <DetailsTab
          websiteData={websiteData}
          formatDate={formatDate}
          onTemplateClick={onTemplateClick}
        />
      )}
    </div>
  );
};

// Related Tab Component (placeholder for now)
const RelatedTab: React.FC = () => {
  return (
    <div className="space-y-3">
      {/* Placeholder for related records */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Related Records</h3>
            <span className="text-xs text-gray-500">(0)</span>
          </div>
        </div>
        <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
          No related records found
        </div>
      </div>
    </div>
  );
};

// Details Tab Component
interface DetailsTabProps {
  websiteData: AuditWebsiteDetailRecord;
  formatDate: (dateStr: string | undefined) => string;
  onTemplateClick?: (templateId: string) => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ websiteData, formatDate, onTemplateClick }) => {
  return (
    <div className="space-y-3">
      {/* Information Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Information</h3>
        </div>
        <div className="p-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Audit Website ID */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Audit Website ID</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{websiteData.name || '-'}</p>
            </div>

            {/* Audit Template */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Audit Template</label>
              {websiteData.templateName ? (
                <button
                  onClick={() => onTemplateClick?.(websiteData.auditTemplateId || '')}
                  className="text-sm text-blue-600 hover:underline dark:text-blue-400 mt-0.5 block text-left"
                >
                  {websiteData.templateName}
                </button>
              ) : (
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                  {websiteData.auditTemplateId || '-'}
                </p>
              )}
            </div>

            {/* Website Configuration */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Website Configuration</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {websiteData.websiteConfigurationName || websiteData.websiteConfigurationId || '-'}
              </p>
            </div>

            {/* Order Override */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Order Override</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {websiteData.orderOverride !== undefined && websiteData.orderOverride !== 0
                  ? websiteData.orderOverride
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System Information Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">System Information</h3>
        </div>
        <div className="p-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Owner */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Owner</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {websiteData.ownerName || websiteData.ownerId || '-'}
              </p>
            </div>

            {/* Record ID */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Record ID</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 font-mono text-xs">
                {websiteData.id || '-'}
              </p>
            </div>

            {/* Salesforce ID */}
            {websiteData.sfId && (
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Salesforce ID</label>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 font-mono text-xs">
                  {websiteData.sfId}
                </p>
              </div>
            )}

            {/* Created By */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {websiteData.createdByName
                  ? `${websiteData.createdByName}, ${formatDate(websiteData.createdDate)}`
                  : websiteData.createdById
                  ? `${websiteData.createdById}, ${formatDate(websiteData.createdDate)}`
                  : formatDate(websiteData.createdDate)}
              </p>
            </div>

            {/* Last Modified By */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Modified By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {websiteData.lastModifiedByName
                  ? `${websiteData.lastModifiedByName}, ${formatDate(websiteData.lastModifiedDate)}`
                  : websiteData.lastModifiedById
                  ? `${websiteData.lastModifiedById}, ${formatDate(websiteData.lastModifiedDate)}`
                  : formatDate(websiteData.lastModifiedDate)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditWebsiteDetail;
